import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonContent,
  IonIcon,
  IonItem,
  IonLoading,
  IonPage,
  IonToast
} from "@ionic/react";
import Header from "../components/Header";
import {ROLE} from "../../enums/ROLE";
import {AuthContext} from "../../providers/authContext";
import {walletOutline} from "ionicons/icons";
import yoneticiService from "../../services/YoneticiService";
import {logError, parseTurkishNumber} from "../../utils";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {SelectItem} from "primereact/selectitem";
import {TahsilatTur} from "../entities/TahsilatTur";
import {Dropdown} from "primereact/dropdown";
import "./YoneticiTahsilatGozlem.css";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {TahsilatTable} from "../entities/TahsilatTable";

const YoneticiKasaDurumu: React.FC = () => {
  const { kurumBilgileri, logo } = React.useContext(AuthContext);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [tahsilatListesi, setTahsilatListesi] = useState<TahsilatTable[]>([]);
  const [selectedTahsilatTur, setSelectedTahsilatTur] = useState(null);
  const [tahsilatTurSelectItems, setTahsilatTurSelectItems] = useState<SelectItem[]>([]);
  const [selectedTahsildar, setSelectedTahsildar] = useState(null);
  const [tahsildarSelectItems, setTahsildarSelectItems] = useState<SelectItem[]>([]);
  const [filtrelenmisTahsilatVerisi, setFiltrelenmisTahsilatVerisi] = useState(tahsilatListesi || []);
  const tahsilatTable = useRef<DataTable>(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    yoneticiService.getTahsilatTurListesi()
      .then((data: TahsilatTur[]) => {
        const items: SelectItem[] = data.map(t => ({
          label: t.aciklama,  // ekranda gözükecek metin
          value: t.tanim      // seçildiğinde kullanılacak değer
        }));
        setTahsilatTurSelectItems(items);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      });
  }, []);

  useEffect(() => {
    yoneticiService.getTahsildarListesi()
      .then((data: string[]) => {
        const items: SelectItem[] = data.map(t => ({
          label: t,
          value: t
        }));
        setTahsildarSelectItems(items);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      });
  }, []);

  const fetchData = () => {
    setShowLoading(true);
    yoneticiService
      .getKasaDurumu()
      .then((data: TahsilatTable[]) => {
        setTahsilatListesi(data);
        setFiltrelenmisTahsilatVerisi(data);
      })
      .catch((error: any) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      })
      .finally(() => setShowLoading(false));
  }

  const tahsilatTurFilter = <Dropdown value={selectedTahsilatTur} options={tahsilatTurSelectItems}
                                          onChange={(e) => {
                                            tahsilatTable.current?.filter(e.value, 'tur', 'equals');
                                            setSelectedTahsilatTur(e.value);
                                        }} placeholder="Hepsi"  className="p-column-filter" showClear style={{ width: '9rem' }} />
  const tahsildarFilter = <Dropdown value={selectedTahsildar} options={tahsildarSelectItems}
                                          onChange={(e) => {
                                            tahsilatTable.current?.filter(e.value, 'adiSoyadi', 'equals');
                                            setSelectedTahsildar(e.value);
                                        }} placeholder="Hepsi"  className="p-column-filter" showClear style={{ minWidth: '9rem' }} />

  const tahsilatGenelToplam = useMemo(() => {
    const veri = filtrelenmisTahsilatVerisi ?? tahsilatListesi ?? [];
    return veri.reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisTahsilatVerisi, tahsilatListesi]);

  const tahsilatFooterGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="GENEL TOPLAM" colSpan={2} footerStyle={{ textAlign: 'right' }} />
        <Column footer={tahsilatGenelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                footerStyle={{ textAlign: 'right', color: 'blue' }} />
      </Row>
    </ColumnGroup>
  );

  return (
    <IonPage>
      <Header
        title={kurumBilgileri?.kisaTanim}
        subtitle=""
        logo={logo}
        roles={[ROLE.Yonetici]}
      />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={"Lütfen Bekleyin..."}
        />
        <IonToast
          isOpen={showToastMessage !== undefined}
          onDidDismiss={() => setShowToastMessage(undefined)}
          message={showToastMessage}
          duration={5000}
          position="top"
        />
        <IonCard>
          <IonCardHeader>
            <IonItem color="light">
              <IonIcon icon={walletOutline} slot="start" />
              Vezne Kasa Durumu
            </IonItem>
          </IonCardHeader>
          <IonCardContent>
            <DataTable ref={tahsilatTable} value={tahsilatListesi}
                       className="p-datatable-sm tahsilat-table"
                       footerColumnGroup={tahsilatFooterGroup}
                       onValueChange={(e) => setFiltrelenmisTahsilatVerisi(e)}
                       stripedRows filterDisplay="row"
                       scrollable showGridlines
                       scrollDirection="horizontal"
                       style={{ minWidth: '100%' }}
                       emptyMessage="Tabloda herhangi bir veri mevcut değil">
              <Column field="adiSoyadi" header="VEZNE"
                      filter
                      filterElement={tahsildarFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false}
                      headerClassName="ion-justify-content-center"
              />
              <Column field="tur" header="TÜR"
                      filter
                      filterElement={tahsilatTurFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false}
                      headerClassName="ion-justify-content-center"
              />
              <Column field="tutar" header="TOPLAM TUTAR"
                      headerClassName="ion-justify-content-center" bodyClassName="ion-justify-content-end mavi-font"/>
            </DataTable>

          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  )
};

export default YoneticiKasaDurumu;
